import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { adminApi } from '@/lib/api';
import { toast } from 'sonner';
import {
  CreditCard, Search, Mail, Globe, ShieldCheck, Loader2,
  CheckCircle2, XCircle, AlertTriangle, RefreshCw, Wrench,
  Webhook,
} from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const INTEGRATIONS = [
  { key: 'stripe',       label: 'Stripe',              icon: CreditCard,
    desc: 'Token packs, subscriptions, Book Store purchases, Weekly Maven.' },
  { key: 'copyleaks',    label: 'Copyleaks',           icon: Search,
    desc: 'Asynchronous plagiarism scanning (per-chapter or full-novel).' },
  { key: 'copyscape',    label: 'Copyscape',           icon: ShieldCheck,
    desc: 'Synchronous web-plagiarism scanning (separate per-user wallet).' },
  { key: 'resend',       label: 'Resend (email)',      icon: Mail,
    desc: 'Master switch for password resets, receipts, alerts, welcome mail, and every outbound message.' },
  { key: 'cloudflare',   label: 'Cloudflare (email)',  icon: Mail,
    desc: 'Alternative transactional email path via a Cloudflare Worker + Email Routing.' },
  { key: 'google_oauth', label: 'Google OAuth',        icon: Globe,
    desc: 'Customer sign-in via Google (The Loom side only — Bookstore uses email/password).' },
];

export default function IntegrationsTogglePanel({ cfg, onSaved }) {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState({});
  useEffect(() => {
    setState({
      stripe: true, copyleaks: true, copyscape: true,
      resend: true, cloudflare: true, google_oauth: true,
      ...(cfg?.integrations_enabled || {}),
    });
  }, [cfg?.integrations_enabled]);
  if (!state) return null;

  const toggle = async (key) => {
    const next = { ...state, [key]: !state[key] };
    setState(next);
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      await adminApi.saveConfig({ integrations_enabled: { [key]: next[key] } });
      toast.success(`${INTEGRATIONS.find((i) => i.key === key).label} ${next[key] ? 'enabled' : 'disabled'}.`);
      onSaved?.();
    } catch (e) {
      setState(state);
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-border bg-card p-4" data-testid="admin-integrations-panel">
        <div className="text-[12.5px] font-semibold">Integrations</div>
        <p className="mt-1 text-[11px] text-muted-foreground">
          Disable an integration to immediately block all related calls (server returns 503)
          and hide the affected UI from customers. Useful when an upstream provider is rate-
          limiting, billing changes, or you&apos;re rotating credentials.
        </p>
        <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
          {INTEGRATIONS.map(({ key, label, icon: Icon, desc }) => (
            <div key={key}
                  data-testid={`admin-integration-row-${key}`}
                  className="flex items-center justify-between gap-3 rounded-md border border-border bg-background p-3">
              <div className="min-w-0">
                <div className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold">
                  <Icon className="h-3.5 w-3.5 text-primary" /> {label}
                </div>
                <p className="text-[10.5px] leading-snug text-muted-foreground">{desc}</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={!!state[key]}
                onClick={() => !saving[key] && toggle(key)}
                data-testid={`admin-integration-toggle-${key}`}
                className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition ${
                  state[key] ? 'bg-primary' : 'bg-secondary'
                } ${saving[key] ? 'opacity-60' : ''}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-card transition ${
                    state[key] ? 'translate-x-4' : 'translate-x-0.5'
                  }`}
                />
                {saving[key] && <Loader2 className="absolute -right-5 h-3 w-3 animate-spin text-muted-foreground" />}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Stripe Webhook Health — live diagnostic panel */}
      <StripeWebhookHealth />
    </div>
  );
}

// ── Stripe Webhook Health ────────────────────────────────────────────────────
function StripeWebhookHealth() {
  const [diag,    setDiag]    = useState(null);
  const [events,  setEvents]  = useState([]);
  const [loading, setLoading] = useState(false);
  const [fixing,  setFixing]  = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dr, er] = await Promise.all([
        axios.get(`${API}/admin/stripe/webhook-diagnostics`, { withCredentials: true }),
        axios.get(`${API}/admin/stripe/recent-events`,       { withCredentials: true }),
      ]);
      setDiag(dr.data);
      setEvents(er.data.events || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load Stripe diagnostics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const fixWebhook = async () => {
    setFixing(true);
    try {
      const { data } = await axios.post(`${API}/admin/stripe/fix-webhook`, {}, { withCredentials: true });
      toast.success(data.ok ? `Webhook registered: ${data.url}` : 'Already healthy');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fix webhook failed');
    } finally {
      setFixing(false);
    }
  };

  const fmt = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(undefined, { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' }); }
    catch { return '—'; }
  };

  return (
    <div className="rounded-md border border-border bg-card p-4" data-testid="stripe-webhook-health">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Webhook className="h-4 w-4 text-primary" />
          <span className="text-[12.5px] font-semibold">Stripe Webhook Health</span>
          {diag && (
            <span className={`ml-1 flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${
              diag.healthy
                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400'
                : diag.using_emergent_key
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400'
                : 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400'
            }`} data-testid="stripe-webhook-status-badge">
              {diag.healthy
                ? <><CheckCircle2 className="h-2.5 w-2.5" /> Healthy</>
                : diag.using_emergent_key
                ? <><CheckCircle2 className="h-2.5 w-2.5" /> Emergent-managed</>
                : <><AlertTriangle className="h-2.5 w-2.5" /> Attention needed</>}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {diag && !diag.healthy && !diag.using_emergent_key && (
            <button
              type="button"
              onClick={fixWebhook}
              disabled={fixing}
              data-testid="stripe-webhook-fix-btn"
              className="flex items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-[11.5px] font-medium text-amber-700 hover:bg-amber-100 transition-colors dark:bg-amber-950/30 dark:border-amber-600/40 dark:text-amber-400"
            >
              {fixing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wrench className="h-3 w-3" />}
              Auto-fix
            </button>
          )}
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="stripe-webhook-refresh-btn"
            className="flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11.5px] text-muted-foreground hover:bg-secondary transition-colors"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {loading && !diag && (
        <div className="mt-4 flex items-center gap-2 text-[12px] text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {diag && (
        <div className="mt-3 space-y-3">
          {/* Configuration status pills */}
          <div className="flex flex-wrap gap-2">
            <StatusPill
              ok={diag.webhook_secret_configured}
              label="Webhook secret"
              testid="stripe-webhook-secret-pill"
            />
            <StatusPill
              ok={diag.using_emergent_key || (diag.registered_endpoints?.some((e) => e.is_correct_url && e.status === 'enabled'))}
              label="Endpoint registered"
              testid="stripe-webhook-endpoint-pill"
            />
            {diag.using_emergent_key && (
              <StatusPill ok label="Emergent-managed key" testid="stripe-emergent-key-pill" />
            )}
          </div>

          {/* Note for Emergent key */}
          {diag.note && (
            <p className="text-[11px] text-muted-foreground">{diag.note}</p>
          )}

          {/* Action items */}
          {(diag.action_needed || []).length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3 dark:bg-amber-950/20 dark:border-amber-700/40">
              <p className="mb-1 text-[11.5px] font-semibold text-amber-700 dark:text-amber-400">Actions needed</p>
              {diag.action_needed.map((a, i) => (
                <p key={i} className="mt-0.5 flex gap-1.5 text-[11px] text-amber-700 dark:text-amber-400">
                  <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{a}
                </p>
              ))}
            </div>
          )}

          {/* Registered endpoints */}
          {(diag.registered_endpoints || []).length > 0 && (
            <div className="space-y-1">
              <p className="text-[11.5px] font-medium">Registered endpoints</p>
              {diag.registered_endpoints.map((ep) => (
                <div key={ep.id} className="flex items-center gap-2 rounded-md border border-border bg-background px-2 py-1.5 text-[11px]" data-testid={`stripe-ep-${ep.id}`}>
                  {ep.is_correct_url && ep.status === 'enabled'
                    ? <CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-500" />
                    : <XCircle className="h-3 w-3 shrink-0 text-amber-500" />}
                  <span className="truncate font-mono text-[10.5px]">{ep.url}</span>
                  <span className={`ml-auto shrink-0 rounded-full px-1.5 py-0.5 text-[9.5px] font-semibold ${
                    ep.status === 'enabled' ? 'bg-emerald-100 text-emerald-700' : 'bg-secondary text-muted-foreground'
                  }`}>{ep.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Recent webhook events */}
      {events.length > 0 && (
        <div className="mt-4">
          <p className="mb-1.5 text-[11.5px] font-medium">Recent events (last {events.length})</p>
          <div className="max-h-48 overflow-y-auto rounded-md border border-border">
            <table className="w-full text-[10.5px]">
              <thead className="sticky top-0 border-b border-border bg-muted/60">
                <tr>
                  <th className="px-2 py-1 text-left font-semibold">Type</th>
                  <th className="px-2 py-1 text-left font-semibold">Received</th>
                  <th className="px-2 py-1 text-center font-semibold">OK</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {events.map((ev, i) => (
                  <tr key={ev.event_id || i} className="hover:bg-muted/30" data-testid={`stripe-event-row-${i}`}>
                    <td className="px-2 py-1 font-mono text-[10px]">{ev.type || '—'}</td>
                    <td className="px-2 py-1 text-muted-foreground">{fmt(ev.received_at)}</td>
                    <td className="px-2 py-1 text-center">
                      {ev.error
                        ? <XCircle className="mx-auto h-3 w-3 text-red-500" />
                        : ev.processed
                        ? <CheckCircle2 className="mx-auto h-3 w-3 text-emerald-500" />
                        : <Loader2 className="mx-auto h-3 w-3 animate-spin text-muted-foreground" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {events.length === 0 && (
            <p className="py-3 text-center text-[11.5px] text-muted-foreground">No webhook events recorded yet.</p>
          )}
        </div>
      )}
    </div>
  );
}

function StatusPill({ ok, label, testid }) {
  return (
    <div
      data-testid={testid}
      className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-medium ${
        ok
          ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:border-emerald-700/40 dark:text-emerald-400'
          : 'border-red-300 bg-red-50 text-red-700 dark:bg-red-950/30 dark:border-red-700/40 dark:text-red-400'
      }`}
    >
      {ok ? <CheckCircle2 className="h-2.5 w-2.5" /> : <XCircle className="h-2.5 w-2.5" />}
      {label}
    </div>
  );
}
